import { useAuthStore } from "../stores/auth";
import { config } from "../config";

export interface ApiResponse<T = any> {
	data: T;
	message?: string;
	success: boolean;
	total?: number;
	page?: number;
	totalPages?: number;
	error?: string;
}

export interface ApiError {
	message: string;
	code?: string;
	details?: any;
}

class ApiClient {
	private baseURL: string;

	constructor(baseURL: string = config.apiBaseUrl) {
		this.baseURL = baseURL;
	}

	private getAuthHeaders(): Record<string, string> {
		const token = useAuthStore.getState().token;
		const headers: Record<string, string> = {
			"Content-Type": "application/json",
		};

		if (token) {
			headers.Authorization = `Bearer ${token}`;
		}

		return headers;
	}

	private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
		if (response.status === 401) {
			// Token hết hạn hoặc không hợp lệ
			useAuthStore.getState().clearAuth();
			throw new Error("Authentication required");
		}

		if (!response.ok) {
			const errorText = await response.text();

			throw new Error(errorText);
		}

		const data = await response.json();
		return data;
	}

	async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
		const url = new URL(`${this.baseURL}${endpoint}`);

		if (params) {
			Object.keys(params).forEach((key) => {
				if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
					url.searchParams.append(key, params[key].toString());
				}
			});
		}
		try {
			const response = await fetch(url.toString(), {
				method: "GET",
				headers: this.getAuthHeaders(),
			});

			return this.handleResponse<T>(response);
		} catch (error) {
			if (error instanceof Error) {
				if (error.message === "Failed to fetch") {
					throw new Error("Kết nối mạng không ổn định hoặc máy chủ không phản hồi.");

				} else {
					throw error;
				}
			} else {
				throw error;
			}
		}

	}

	async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
		try {
			const response = await fetch(`${this.baseURL}${endpoint}`, {
				method: "POST",
				headers: this.getAuthHeaders(),
				body: data ? JSON.stringify(data) : undefined,
			});

			return this.handleResponse<T>(response);
		} catch (error) {
			if (error instanceof Error) {
				if (error.message === "Failed to fetch") {
					throw new Error("Kết nối mạng không ổn định hoặc máy chủ không phản hồi.");

				} else {
					throw error;
				}
			} else {
				throw error;
			}
		}
	}

	async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
		try {
			const response = await fetch(`${this.baseURL}${endpoint}`, {
				method: "PUT",
				headers: this.getAuthHeaders(),
				body: data ? JSON.stringify(data) : undefined,
			});

			return this.handleResponse<T>(response);
		} catch (error) {
			if (error instanceof Error) {
				if (error.message === "Failed to fetch") {
					throw new Error("Kết nối mạng không ổn định hoặc máy chủ không phản hồi.");

				} else {
					throw error;
				}
			} else {
				throw error;
			}
		}
	}

	async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
		try {
			const response = await fetch(`${this.baseURL}${endpoint}`, {
				method: "PATCH",
				headers: this.getAuthHeaders(),
				body: data ? JSON.stringify(data) : undefined,
			});

			return this.handleResponse<T>(response);
		} catch (error) {
			if (error instanceof Error) {
				if (error.message === "Failed to fetch") {
					throw new Error("Kết nối mạng không ổn định hoặc máy chủ không phản hồi.");

				} else {
					throw error;
				}
			} else {
				throw error;
			}
		}
	}

	async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
		try {
			const response = await fetch(`${this.baseURL}${endpoint}`, {
				method: "DELETE",
				headers: this.getAuthHeaders(),
			});

			return this.handleResponse<T>(response);
		} catch (error) {
			if (error instanceof Error) {
				if (error.message === "Failed to fetch") {
					throw new Error("Kết nối mạng không ổn định hoặc máy chủ không phản hồi.");

				} else {
					throw error;
				}
			} else {
				throw error;
			}
		}
	}
}

export const apiClient = new ApiClient();
export default apiClient;
